import { Backdrop, makeStyles, Modal } from '@material-ui/core'
import { TextArea } from '@progress/kendo-react-inputs'
import { useEffect, useState } from 'react'
import styles from './GenericModals.module.scss'
import { BUTTON_TYPES, AWCButton, FastQuoteInput } from '../Inputs/Inputs'
import { validEmail } from '../Validator'

const useStyles = makeStyles((theme) => ({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        outline: 0,
    },
    paper: {
        backgroundColor: theme.palette.background.paper,
        // border: '2px solid #000',
        boxShadow: theme.shadows[5],
        padding: theme.spacing(2, 4, 3),
    },
}))

interface TextAreaModalProps {
    open: boolean
    title: string
    onSubmit?(newString: string): void
    close(): void
    defaultValue?: string
    zeroPrice?: number
}

//TODO maybe accept a class parameter
export const TextAreaModal = (props: TextAreaModalProps) => {
    const classes = useStyles()
    const { open, onSubmit, close, title, zeroPrice } = props
    const [value, setValue] = useState(props.defaultValue || '')
    const [textButtonClicked, setTextButtonClicked] = useState<boolean>(false)
    const [focused, setFocused] = useState(false)
    useEffect(() => {
        setValue(props.defaultValue || '')
    }, [props.defaultValue])
    // clear out on close
    useEffect(() => {
        if (open && zeroPrice === 0) {
            setTextButtonClicked(false)
        }
        !open && setValue(props.defaultValue || '')
        setFocused(false)
    }, [open])
    // useEffect(() => {
    //     if (zeroPrice === 0) {
    //         setTextButtonClicked(false)
    //     }
    // }, [zeroPrice])
    return (
        <Modal
            open={open}
            className={classes.modal}
            onClose={() => {
                close()
            }}
            BackdropComponent={Backdrop}
        >
            <div className={styles.modalContainer}>
                <div
                    className={styles.lineItemNotesModal}
                    data-testid={'lineItemNotesModal'}
                    style={{ minWidth: 800 }}
                >
                    {/* Line Item Notes Header */}
                    <div className={styles.lineItemNotesModalHeader}>
                        <div className={styles.lineItemNotesModalTitle}>{title}</div>
                        <div onClick={close} className={'a-i-close'} />
                    </div>
                    <div className={'a-divider2'} />
                    {zeroPrice === 0 && !textButtonClicked ? (
                        <div>
                            <div style={{ marginTop: 10, marginBottom: 10 }}>
                                Line Item Notes are required for a zero sales price.
                            </div>
                            <div
                                style={{
                                    marginBottom: 5,
                                    display: 'flex',
                                    position: 'relative',
                                    alignItems: 'center',
                                    gap: 30,
                                }}
                            >
                                <div>Start notes with:</div>
                                <AWCButton
                                    type={'secondary'}
                                    onClick={() => {
                                        let v = value
                                        setValue('Will Advise\n' + v)
                                        setTextButtonClicked(true)
                                    }}
                                    style={{
                                        width: 84,
                                        fontWeight: 400,
                                        fontSize: 14,
                                    }}
                                >
                                    Will Advise
                                </AWCButton>
                                <AWCButton
                                    type={'secondary'}
                                    onClick={() => {
                                        let v = value
                                        setValue('Price included in Line Item above\n' + v)
                                        setTextButtonClicked(true)
                                    }}
                                    style={{
                                        width: 220,
                                        fontWeight: 400,
                                        fontSize: 14,
                                    }}
                                >
                                    Price included in Line Item above
                                </AWCButton>
                            </div>
                        </div>
                    ) : (
                        <div
                            style={{
                                marginBottom: 5,
                                display: 'flex',
                                position: 'relative',
                                alignItems: 'end',
                                height: 35,
                            }}
                        >
                            Notes
                        </div>
                    )}

                    <div style={{ display: 'flex', marginBottom: 20 }}>
                        <TextArea
                            rows={12}
                            ref={(ref) => {
                                // Without this the focus will go to the start
                                if (
                                    ref &&
                                    !focused &&
                                    document.activeElement !== ref.element.current
                                ) {
                                    ref.element.current!.focus()
                                    ref.element.current!.setSelectionRange(
                                        value.length,
                                        value.length
                                    )
                                    setFocused(true)
                                }
                            }}
                            style={{
                                minWidth: 800,
                                borderWidth: 1,
                                borderColor: 'lightgray',
                                backgroundColor: 'white',
                            }}
                            // value={tempNotes}
                            onChange={(event: any) => {
                                setValue(event.value)
                            }}
                            value={value}
                            readOnly={onSubmit === undefined}
                        />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: onSubmit ? '110px 110px' : 'none',
                                marginTop: 10,
                            }}
                        >
                            {onSubmit && (
                                <AWCButton
                                    type={'secondary'}
                                    // onClick={onConfirmClick}
                                    onClick={close}
                                    style={{
                                        width: 94,
                                    }}
                                    data-testid='lineItemNotesModalCancelButton'
                                >
                                    Cancel
                                </AWCButton>
                            )}
                            <AWCButton
                                type={BUTTON_TYPES.PRIMARY}
                                // onClick={onConfirmClick}
                                onClick={() => {
                                    onSubmit && onSubmit(value)
                                    close()
                                }}
                                style={{
                                    width: 94,
                                }}
                                data-testid='lineItemNotesModalSubmitButton'
                            >
                                Done
                            </AWCButton>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    )
}

interface TextInputModalProps {
    open: boolean
    title: string
    cancelText?: string
    submitText?: string
    onSubmit(newString: string): void
    close(): void
}

export const TextInputModal = (props: TextInputModalProps) => {
    const { open, title, cancelText, submitText, onSubmit, close } = props
    const classes = useStyles()
    const [email, setEmail] = useState<string>('')
    const [isInvalidEmail, setIsInvalidEmail] = useState<boolean>(false)

    return (
        <Modal
            open={open}
            className={classes.modal}
            onClose={() => {
                close()
            }}
            BackdropComponent={Backdrop}
        >
            <div className={styles.modalContainer}>
                <div className={styles.textInputModal}>
                    {/* Modal Header */}
                    <div className={styles.textInputModalHeader}>
                        <div className={styles.lineItemNotesModalTitle}>{title}</div>
                        <div onClick={close} className={'a-i-close'} />
                    </div>
                    <div className={'a-divider2'} />

                    <div style={{ marginBottom: 20, marginTop: 20 }}>
                        <FastQuoteInput
                            onChange={(value: string) => {
                                setEmail(value)
                                setIsInvalidEmail(false)
                            }}
                            title={'Email'}
                            value={email}
                            autoFocus
                            placeholder={'example@email.com'}
                            maxLength={50}
                            validationText={email && isInvalidEmail ? 'Invalid Email' : ''}
                        />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '110px 110px',
                                marginTop: 10,
                            }}
                        >
                            {cancelText && (
                                <AWCButton
                                    type={'secondary'}
                                    onClick={close}
                                    style={{
                                        width: 94,
                                    }}
                                >
                                    {cancelText}
                                </AWCButton>
                            )}
                            {submitText && (
                                <AWCButton
                                    type={
                                        email && !isInvalidEmail
                                            ? BUTTON_TYPES.PRIMARY
                                            : BUTTON_TYPES.DISABLED
                                    }
                                    onClick={
                                        email && !isInvalidEmail
                                            ? () => {
                                                  const valid = validEmail(email)
                                                  if (!valid) {
                                                      setIsInvalidEmail(true)
                                                  } else {
                                                      onSubmit(email)
                                                      close()
                                                  }
                                              }
                                            : () => {}
                                    }
                                    style={{
                                        width: 94,
                                    }}
                                >
                                    {submitText}
                                </AWCButton>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    )
}

export interface ConfirmModalProps {
    title: string
    text: string
    onCancel(): void
    onConfirm(): void
}
export const ConfirmModal = (props: ConfirmModalProps) => {
    const { title, text, onCancel, onConfirm } = props
    const classes = useStyles()

    return (
        <Modal
            open={true}
            className={classes.modal}
            onClose={onCancel}
            BackdropComponent={Backdrop}
        >
            <div className={styles.modalContainer} style={{ borderRadius: 2 }}>
                <div className={styles.lineItemNotesModal} data-testid={'lineItemNotesModal'}>
                    <div className={styles.lineItemNotesModalHeader}>
                        <div className={styles.lineItemNotesModalTitle}>{title}</div>
                        <div onClick={onCancel} className={'a-i-close'} />
                    </div>
                    <div className={'a-divider'} style={{ marginBottom: 15 }} />
                    <div style={{ display: 'flex', marginBottom: 20 }}>{text}</div>

                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: '110px 110px',
                            marginTop: 10,
                        }}
                    >
                        <div
                            className={'a-cancel-button'}
                            style={{
                                width: 94,
                            }}
                            onClick={onCancel}
                            data-testid='lineItemCancelDeleteButton'
                        >
                            Cancel
                        </div>
                        <AWCButton
                            type={BUTTON_TYPES.PRIMARY}
                            onClick={onConfirm}
                            style={{
                                width: 94,
                            }}
                            data-testid='lineItemConfirmDeleteButton'
                        >
                            Confirm
                        </AWCButton>
                    </div>
                </div>
            </div>
        </Modal>
    )
}

export interface DialogProps {
    title: string
    onCancel(): void
    cancelText?: string
    onConfirm(): void
    confirmText?: string
    children: any
    style?: React.CSSProperties
}
export const Dialog = (props: DialogProps) => {
    const {
        title,
        onCancel,
        onConfirm,
        children,
        style,
        cancelText = 'Cancel',
        confirmText = 'Confirm',
    } = props
    const classes = useStyles()

    return (
        <Modal
            open={true}
            className={classes.modal}
            onClose={onCancel}
            BackdropComponent={Backdrop}
        >
            <div className={styles.modalContainer} style={style}>
                <div className={styles.modal} data-testid={'lineItemNotesModal'}>
                    <div
                        className={styles.modalHeader}
                        style={{
                            borderRadius: '4px 4px 0px 0px',
                            padding: '17px 25px',
                        }}
                    >
                        <div className={styles.modalTitle} style={{ fontSize: 18 }}>
                            {title}
                        </div>
                    </div>
                    <div className={styles.modalBody}>
                        <div style={{ marginBottom: 34 }}>{children}</div>

                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                marginTop: 10,
                                gap: 12,
                                justifyContent: 'flex-end',
                            }}
                        >
                            <AWCButton
                                type={BUTTON_TYPES.SECONDARY}
                                style={{
                                    width: '100%',
                                }}
                                onClick={onCancel}
                                data-testid='lineItemCancelDeleteButton'
                            >
                                {cancelText}
                            </AWCButton>
                            <AWCButton
                                type={BUTTON_TYPES.PRIMARY}
                                onClick={onConfirm}
                                style={{
                                    width: '100%',
                                }}
                                data-testid='lineItemConfirmDeleteButton'
                            >
                                {confirmText}
                            </AWCButton>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    )
}

export interface ButtonInfo {
    type: BUTTON_TYPES
    onClick(): void
    text: string
}
export interface CustomButtonDialogProps {
    title: string
    buttons: ButtonInfo[]
    onClose(): void
    children: any
    style?: React.CSSProperties
}
export const CustomButtonDialog = (props: CustomButtonDialogProps) => {
    const { title, children, style, buttons, onClose } = props
    const classes = useStyles()

    return (
        <Modal open={true} className={classes.modal} onClose={onClose} BackdropComponent={Backdrop}>
            <div className={styles.modalContainer} style={style}>
                <div className={styles.modal} data-testid={'lineItemNotesModal'}>
                    <div
                        className={styles.modalHeader}
                        style={{
                            borderRadius: '4px 4px 0px 0px',
                            padding: '17px 25px',
                        }}
                    >
                        <div className={styles.modalTitle} style={{ fontSize: 18 }}>
                            {title}
                        </div>
                    </div>
                    <div className={styles.modalBody}>
                        <div>{children}</div>

                        <div
                            style={{
                                display: 'flex',
                                marginTop: 10,
                                gap: 12,
                                justifyContent: 'flex-end',
                                padding: '15px 0px 11px',
                            }}
                        >
                            {buttons.map((row, idx) => (
                                <AWCButton
                                    type={row.type}
                                    style={{
                                        width: '100%',
                                    }}
                                    onClick={() => {
                                        row.onClick()
                                        onClose()
                                    }}
                                    key={`lineItemCancelDeleteButtonCustomButtonDialog${idx}`}
                                    data-testid='lineItemCancelDeleteButton'
                                >
                                    {row.text}
                                </AWCButton>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    )
}
